import type { CSSProperties, ReactNode } from 'react';

interface EyebrowProps {
  children: ReactNode;
  className?: string;
  /** Cor custom (use só pra contextos dark). Default = navy 50%. */
  color?: string;
}

/** Micro-label uppercase com tracking solto — usado acima de displays. */
export function Eyebrow({ children, className = '', color }: EyebrowProps) {
  return (
    <span
      className={`text-[11px] font-semibold uppercase tracking-[0.06em] ${className}`}
      style={color ? { color } : { color: 'rgba(0,27,116,0.5)' }}
    >
      {children}
    </span>
  );
}

interface DisplayProps {
  children: ReactNode;
  size?: number;
  weight?: 400 | 500 | 600 | 700;
  className?: string;
  style?: CSSProperties;
  as?: 'h1' | 'h2' | 'h3';
}

/** Display heading com tracking apertado. Usar pra hero copy / headline. */
export function Display({
  children,
  size = 32,
  weight = 500,
  className = '',
  style,
  as: Tag = 'h1'
}: DisplayProps) {
  return (
    <Tag
      className={`m-0 leading-[1.05] ${className}`}
      style={{
        fontFamily: 'Chillax, system-ui, sans-serif',
        fontWeight: weight,
        fontSize: size,
        letterSpacing: '-0.025em',
        ...style
      }}
    >
      {children}
    </Tag>
  );
}

interface MoneyDisplayProps {
  value: number | string;
  size?: number;
  weight?: 400 | 500 | 600 | 700;
  className?: string;
  style?: CSSProperties;
}

/**
 * Renderiza valores BRL com R$ + centavos em corpo menor (ratio 0.5),
 * número principal em Chillax tracking apertado. Ideal pra cards de saldo
 * e totais de recibo.
 */
export function MoneyDisplay({
  value,
  size = 40,
  weight = 600,
  className = '',
  style
}: MoneyDisplayProps) {
  const num = typeof value === 'string' ? Number(value) : value;
  // Saldo pode ser NEGATIVO (pendência, migration 049). Sem tratar o sinal,
  // Math.floor(-24.40) = -25 e os centavos saíam "-40" → "R$ -25,-40".
  const negative = num < 0;
  const abs = Math.abs(num);
  const cents = Math.round((abs % 1) * 100)
    .toString()
    .padStart(2, '0');
  const reais = Math.floor(abs).toLocaleString('pt-BR');
  return (
    <span
      className={className}
      style={{
        fontFamily: 'Chillax, system-ui, sans-serif',
        fontWeight: weight,
        fontSize: size,
        lineHeight: 1,
        letterSpacing: '-0.03em',
        ...style
      }}
    >
      {negative && '− '}
      <span style={{ fontSize: size * 0.5, opacity: 0.55, marginRight: 4, fontWeight: 500 }}>
        R$
      </span>
      {reais}
      <span style={{ fontSize: size * 0.5, opacity: 0.55, fontWeight: 500 }}>,{cents}</span>
    </span>
  );
}
